import React, { useEffect, useState } from 'react';
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from '@mui/material/Grid';
import * as dates from '@simplybenefits/sb-dates';
import PropTypes from 'prop-types';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import DateInput from '../../TextFields/DateInput.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import EffectiveDateModal from '../EffectiveDateModal.js';
import WaiveForBubbles from './WaiveForBubble.js';
import WaivedBenefitsBubbles from './WaivedBenefitsBubbles.js';
import { validateInput } from '../../../services/index.js';
import { parseUserDataForAddUpdate } from '../EmployeeDetailsModal/parseEmployeeData.js';
const initialState = {
  startDate: '',
  endDate: '',
  acceptedTerms: false
};
const initialErrorState = {
  startDateError: false,
  endDateError: false,
  acceptedTermsError: false
};

/**
 * Modal component for reinstating coverage.
 * Allows users to select coverage options, enter dates, and submit reinstatement requests.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {function} props.close - Function to close the modal
 * @param {function} props.openChangeRequestModal - Function to open change request modal
 * @param {function} props.reinstateCoverage - Function to reinstate coverage (used in master site)
 * @param {Object} props.userData - User data object
 * @param {Object} props.classData - Class data object
 * @param {Array} props.divisions - Array of divisions
 * @param {boolean} props.isStudent - Indicates if the user is a student
 * @param {boolean} props.isMasterSite - Indicates if this is the master site
 * @param {function} props.loader - Loader function
 */
const ReinstateCoverageModal = ({
  open,
  close,
  openChangeRequestModal,
  reinstateCoverage,
  // need these props below when using this modal from the master site
  userData,
  classData,
  divisions,
  isStudent,
  isMasterSite = false,
  loader
}) => {
  // State initialization
  const [reinstateFor, setReinstateFor] = useState(false);
  const [showEffectiveDateModal, setShowEffectiveDateModal] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [effectiveDateError, setEffectiveDateError] = useState(false);
  const [hasWaivedBenefits, setHasWaivedBenefits] = useState(false);
  const [comparableCoverage, setComparableCoverage] = useState(initialState);
  const [errorState, setErrorState] = useState(initialErrorState);

  // Destructure error states and comparable coverage for easier access
  const {
    startDateError,
    endDateError,
    acceptedTermsError,
    reinstateForError,
    hasWaivedBenefitsError
  } = errorState;
  const {
    startDate,
    endDate,
    acceptedTerms
  } = comparableCoverage;

  // Reset hasWaivedBenefits when reinstateFor changes
  useEffect(() => {
    if (!reinstateFor) {
      setHasWaivedBenefits(false);
    }
  }, [reinstateFor]);

  // Reset form state when modal opens/closes
  useEffect(() => {
    setReinstateFor(false);
    setHasWaivedBenefits(false);
    setErrorState(initialErrorState);
  }, []);

  /**
   * Handles changes to input fields
   * @param {string} field - The field being updated
   * @param {*} value - The new value for the field
   */
  const handleInputChange = (field, value) => {
    setComparableCoverage(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Resets the form to its initial state
   */
  const resetForm = () => {
    setReinstateFor(false);
    setHasWaivedBenefits(false);
    setErrorState(initialErrorState);
    setComparableCoverage(initialState);
  };

  /**
   * Calculates the new waive values based on user selections
   * This function determines which benefits are being reinstated and updates the waive status accordingly.
   * 
   * @returns {{hasWaived: string|null, waiveFor: string|null}}
   *   hasWaived: A string of benefits still waived after reinstatement, or null if all benefits are reinstated
   *   waiveFor: Indicates who the remaining waived benefits apply to ('Dependents' or null)
   */
  const returnWaiveValues = () => {
    const hasWaivedOriginal = userData.hasWaived;
    const benefitsOriginal = hasWaivedOriginal.split('-');
    const benefitsToReinstate = hasWaivedBenefits.split('-');
    const [newHasWaived = null] = benefitsOriginal.filter(e => !benefitsToReinstate.includes(e));
    let hasWaived = null;
    let waiveFor = null;
    if (reinstateFor === 'Self-Dependents') {
      hasWaived = null;
      waiveFor = null;
    }
    if (reinstateFor === 'Dependents') {
      hasWaived = newHasWaived;
      waiveFor = newHasWaived === null ? null : 'Dependents';
    }
    if (reinstateFor === 'Myself-Only') {
      hasWaived = hasWaivedOriginal;
      waiveFor = 'Dependents';
    }
    return {
      hasWaived,
      waiveFor
    };
  };

  /**
   * Prepares comparable coverage data for submission
   * This function assembles the final data object for comparable coverage,
   * including waive status and related coverage information.
   * 
   * @returns {Object} Comparable coverage data
   *   waiveFor: Who the waived benefits apply to ('Dependents' or null) (we are reinstating benefits, so this value need to be set to null in the DB)
   *   hasWaived: String of waived benefits or null
   *   comparableCoverageEmployer: (null if no waived benefits)
   *   comparableCoverageCarrier: (null if no waived benefits)
   *   comparableCoverageCert: (null if no waived benefits)
   */
  const getComparableCoverageData = () => {
    const waiveValuesResult = returnWaiveValues();
    const comparableCoverageFields = ['comparableCoverageEmployer', 'comparableCoverageCarrier', 'comparableCoverageCert'];
    const comparableCoverageObj = [waiveValuesResult.waiveFor, waiveValuesResult.hasWaived].every(e => e === null) ? Object.fromEntries(comparableCoverageFields.map(e => [e, null])) : {};
    const comparableCoverageData = {
      waiveFor: waiveValuesResult.waiveFor,
      hasWaived: waiveValuesResult.hasWaived,
      ...comparableCoverageObj
    };
    return comparableCoverageData;
  };

  /**
   * Submits the reinstatement request
   * @returns {Promise<boolean>} Success status of the submission
   */
  const submit = async () => {
    const noFutureDates = false;
    const noPastDates = false;
    const mustBeFirstOfMonth = false;
    const error = dates.validate(effectiveDate, noFutureDates, noPastDates, mustBeFirstOfMonth, i18n.locale);
    if (error) {
      return setEffectiveDateError(error);
    }
    const isEdit = true; // need to set this variable to return the correct data from the parsing function
    const isProfile = true; // this is called from the master site from the member profile, which requires sending some extra data
    const data = parseUserDataForAddUpdate(userData, classData, divisions, isStudent, isMasterSite, isEdit, isProfile);
    const comparableCoverageData = getComparableCoverageData();
    const updateData = {
      ...data,
      ...comparableCoverageData,
      effectiveDate: dates.getUTC(effectiveDate)
    };
    const success = await reinstateCoverage(userData.userID, updateData, isProfile);
    if (success) {
      close();
      resetForm();
      return true;
    }
    return false;
  };

  /**
   * Validates the form inputs
   * @returns {Promise<Object|boolean>} Validation errors or false if valid
   */
  const validateForm = async () => {
    let errors = {};
    errors.reinstateForError = !reinstateFor ? i18n.t(tokens.labels.validations.pleaseSelectOneLabel) : null;
    errors.hasWaivedBenefitsError = !hasWaivedBenefits ? i18n.t(tokens.labels.validations.pleaseSelectOneLabel) : null;
    errors.startDateError = startDate ? await validateInput('date', startDate, i18n.locale) : false;
    const noFutureDates = false;
    const noPastDates = false;
    const mustBeFirstOfMonth = false;
    errors.endDateError = startDate || endDate ? dates.validate(endDate, noFutureDates, noPastDates, mustBeFirstOfMonth, i18n.locale) : false;
    errors.acceptedTermsError = !acceptedTerms;
    const hasErrors = Object.values(errors).some(Boolean); // Check for any errors
    return hasErrors ? errors : false;
  };

  /**
   * Validates form and processes reinstatement or opens change request modal
   * @returns {Promise<boolean>} Success status of the operation
   */
  const validateAndProcessReinstatement = async () => {
    const formErrors = await validateForm();
    if (formErrors) {
      setErrorState({
        ...formErrors
      });
      return false;
    }
    if (isMasterSite) {
      return setShowEffectiveDateModal(true);
    }
    const comparableCoverageData = getComparableCoverageData();
    const changeRequestData = {
      type: 'Reinstate Benefits',
      details: {
        ...comparableCoverageData,
        startDate: startDate ? dates.getUTC(startDate) : null,
        endDate: endDate ? dates.getUTC(endDate) : null
      }
    };
    openChangeRequestModal(changeRequestData);
    resetForm(); // reset form on creation of change request
    return true;
  };

  /**
   * Handles modal close action
   */
  const onClose = () => {
    resetForm();
    close();
  };

  // Render effective date modal if showEffectiveDateModal is true
  if (showEffectiveDateModal) {
    return /*#__PURE__*/React.createElement(EffectiveDateModal, {
      open: showEffectiveDateModal,
      onClose: () => setShowEffectiveDateModal(false),
      effectiveDate: effectiveDate,
      setEffectiveDate: setEffectiveDate,
      effectiveDateError: effectiveDateError,
      loader: loader,
      employeeMeta: userData,
      isMasterProfile: isMasterSite,
      onClick: submit
    });
  }

  // Render main reinstate coverage modal
  return /*#__PURE__*/React.createElement(Modal, {
    medium: true,
    open: open,
    onClose: onClose,
    title: i18n.t(tokens.labels.changeRequests.reinstateCoverage),
    primaryButton: {
      label: i18n.t(tokens.labels.changeRequests.reinstateCoverage),
      onClick: validateAndProcessReinstatement
    }
  }, /*#__PURE__*/React.createElement(Text, {
    small: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.youHaveWaivedCoverage)), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true,
    margin: "20px 0 10px 0"
  }, i18n.t(tokens.labels.changeRequests.reinstatingCoverageFor)), /*#__PURE__*/React.createElement(WaiveForBubbles, {
    userData: userData,
    reinstateFor: reinstateFor,
    reinstateForError: reinstateForError,
    setReinstateFor: setReinstateFor
  }), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true,
    margin: "20px 0 10px 0"
  }, i18n.t(tokens.labels.signup.forTheFollowingBenefits)), /*#__PURE__*/React.createElement(WaivedBenefitsBubbles, {
    userData: userData,
    hasWaivedBenefits: hasWaivedBenefits,
    hasWaivedBenefitsError: hasWaivedBenefitsError,
    setHasWaivedBenefits: setHasWaivedBenefits
  }), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      marginTop: 20,
      marginBottom: 10
    }
  }, i18n.t(tokens.labels.changeRequests.reinstatingDueTo)), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    columnSpacing: 2
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.changeRequests.dateStart),
    value: startDate,
    onChange: e => handleInputChange('startDate', e.target.value),
    error: startDateError,
    fullWidth: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.changeRequests.dateEnd),
    value: endDate,
    onChange: e => handleInputChange('endDate', e.target.value),
    error: endDateError,
    fullWidth: true
  }))), /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: true
  }, /*#__PURE__*/React.createElement(FormGroup, {
    row: true
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: Boolean(acceptedTerms),
      onChange: () => handleInputChange('acceptedTerms', !acceptedTerms)
    }),
    label: /*#__PURE__*/React.createElement(Text, {
      avMedium: true
    }, i18n.t(tokens.labels.changeRequests.confirmReinstateCoverage))
  })), acceptedTermsError ? /*#__PURE__*/React.createElement(FormHelperText, {
    error: true
  }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)) : null));
};
ReinstateCoverageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  reinstateCoverage: PropTypes.func,
  userData: PropTypes.shape({
    userID: PropTypes.string.isRequired,
    hasWaived: PropTypes.string.isRequired,
    waiveFor: PropTypes.string.isRequired
  }).isRequired,
  classData: PropTypes.object,
  divisions: PropTypes.object,
  isStudent: PropTypes.bool,
  isMasterSite: PropTypes.bool,
  loader: PropTypes.func
};
export default ReinstateCoverageModal;